// Fetching your own archived games, and the presentation helpers the profile page shares with
// its stats/table/chart components.

import { useEffect, useState } from "react";
import { getBaseCharacter, type HistoryGame, type HistoryResponse, type Team } from "@cc/shared";
import { useAuth } from "../state/auth.js";

export type HistoryLoad =
  | { status: "loading"; games: HistoryGame[] }
  | { status: "ready"; games: HistoryGame[] }
  | { status: "error"; games: HistoryGame[]; error: string };

/** Loads the games you played or ran, once per token. */
export function useHistory(): HistoryLoad {
  const token = useAuth((s) => s.token);
  const [load, setLoad] = useState<HistoryLoad>({ status: "loading", games: [] });

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoad({ status: "loading", games: [] });
    fetch("/api/profile/history", { headers: { authorization: `Bearer ${token}` } })
      .then(async (res) => {
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error ?? "Could not load history.");
        }
        return (await res.json()) as HistoryResponse;
      })
      .then((body) => {
        if (!cancelled) setLoad({ status: "ready", games: body.games });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setLoad({ status: "error", games: [], error: err instanceof Error ? err.message : "Could not load history." });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return load;
}

/** Icons exist for the base roster only; a custom character shows its name with no picture. */
export function characterIcon(characterId: string | null): string | null {
  return characterId ? (getBaseCharacter(characterId)?.icon ?? null) : null;
}

const dateFormat = new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short", year: "numeric" });

export function formatDate(iso: string): string {
  return dateFormat.format(new Date(iso));
}

/** Chart colour for a team: side, not team — the icon beside a bar already says which role it was. */
export function sideColorClass(team: Team | null): string {
  if (team === "townsfolk" || team === "outsider") return "bg-good";
  if (team === "minion" || team === "demon") return "bg-evil";
  return "bg-ochre";
}

export function percent(part: number, whole: number): string {
  if (whole === 0) return "—";
  return `${Math.round((part / whole) * 100)}%`;
}
